import { useState } from 'react';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import { useSetAorCoverage } from 'hooks/useAorCoverage';

type AorOption = { label: string; value: string };

type Props = {
  groupGuid: string;
  userGuid: string;
  aorOptions: AorOption[];
  coveredAorItemIds: string[];
  canEdit: boolean;
};

/**
 * The areas a member covers, which is what geo-targeted notifications resolve
 * against.
 *
 * Separate from Home AOR, which is one place a person is based. Coverage is a
 * set, because the people worth notifying about a flood are the ones
 * responsible for the affected ground, and that is rarely exactly one area.
 */
export const MemberAorCoverage: React.FC<Props> = ({
  groupGuid,
  userGuid,
  aorOptions,
  coveredAorItemIds,
  canEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const setCoverage = useSetAorCoverage(groupGuid);

  const selected = aorOptions.filter((option) =>
    coveredAorItemIds.includes(option.value)
  );

  return (
    <div className="mb-3">
      <div className="d-flex align-items-baseline justify-content-between">
        <label className="form-label d-block mb-0">Area Coverage</label>
        {canEdit ? (
          <button
            type="button"
            className="button button--link"
            onClick={() => setIsEditing((editing) => !editing)}
          >
            {isEditing ? 'Done' : 'Edit'}
          </button>
        ) : null}
      </div>
      {isEditing ? (
        <StylishNewSelect
          options={aorOptions}
          value={selected}
          onChange={(next: AorOption[] | null) =>
            setCoverage.mutate({
              userGuid,
              aorItemIds: (next ?? []).map((option) => option.value),
            })
          }
          placeholder="Select areas this member covers"
          isMulti
          isClearable
          isSearchable
          isDisabled={setCoverage.isPending}
        />
      ) : (
        <span className="tw-ps-3 fst-italic">
          {selected.length > 0
            ? selected.map((option) => option.label).join(', ')
            : 'None'}
        </span>
      )}
    </div>
  );
};

export default MemberAorCoverage;
